import type { BrandedLoginShellProps } from './types';

/**
 * BrandedLoginShell - A reusable login shell component for Helios-derived products.
 *
 * Layout:
 * - Left 2/3: Information panel with org branding, welcome message, and features
 * - Right 1/3: Floating login card with org logo, form slot, and support info
 *
 * This component is product-agnostic. It uses generic "Workspace" terminology
 * and accepts branding configuration to customize appearance per client.
 *
 * Styling: Exposes className hooks for Tailwind customization without forking.
 */
export function BrandedLoginShell({
  branding,
  children,
  subtitle = 'Sign in to your workspace',
  errorText,
  isLoading,
  leftSlot,
  className = '',
  infoPanelClassName = '',
  loginCardClassName = '',
}: BrandedLoginShellProps) {
  const {
    orgName,
    orgLogoUrl,
    orgLogoHorizontalUrl,
    poweredBy,
    supportEmail,
    supportPhone,
    theme,
  } = branding;
  const poweredByLabel = poweredBy ?? 'Helios Core';

  const resolvedLeftSlot = typeof leftSlot === 'function' ? leftSlot(branding) : leftSlot;

  // Derive theme values
  const primaryColor = theme?.primaryColor ?? '#2563eb';
  const backgroundWash = theme?.backgroundWash ?? 'gradient';

  // Soft pastel background based on primary color
  const getInfoPanelStyle = (): React.CSSProperties => {
    const lightPrimary = adjustColor(primaryColor, 80);
    const mediumPrimary = adjustColor(primaryColor, 40);

    switch (backgroundWash) {
      case 'solid':
        return { backgroundColor: lightPrimary };
      case 'mixed':
        return {
          background: `linear-gradient(135deg, ${lightPrimary} 0%, ${mediumPrimary} 100%)`,
        };
      case 'blue':
        return {
          background: `linear-gradient(160deg, #eff6ff 0%, #e0f2fe 50%, #f8fbff 100%)`,
        };
      case 'gradient':
      default:
        return {
          background: `linear-gradient(160deg, #e0f2fe 0%, #fce7f3 50%, #f0fdf4 100%)`,
        };
    }
  };

  return (
    <div
      data-login-shell
      className={`h-screen w-full bg-slate-50 overflow-hidden ${className}`}
      style={
        {
          '--brand-primary': primaryColor,
          '--brand-primary-hover': adjustColor(primaryColor, -15),
        } as React.CSSProperties
      }
    >
      {/* Left 2/3: Information Panel - Hidden on mobile */}
      <div
        data-info-panel
        className={`relative overflow-hidden ${infoPanelClassName}`}
        style={resolvedLeftSlot ? undefined : { ...getInfoPanelStyle() }}
      >
        {/* Decorative blur shapes */}
        {!resolvedLeftSlot && (
          <div
            className="absolute inset-0 overflow-hidden pointer-events-none"
            style={{ zIndex: 0, pointerEvents: 'none' }}
          >
            <div
              className="absolute rounded-full opacity-30 blur-3xl"
              style={{
                backgroundColor: primaryColor,
                width: '24rem',
                height: '24rem',
                top: '-6rem',
                left: '-6rem',
                pointerEvents: 'none',
              }}
            />
            <div
              className="absolute rounded-full opacity-20 blur-3xl"
              style={{
                backgroundColor: adjustColor(primaryColor, 40),
                width: '20rem',
                height: '20rem',
                top: '33%',
                right: '0',
                pointerEvents: 'none',
              }}
            />
            <div
              className="absolute rounded-full opacity-20 blur-3xl"
              style={{
                backgroundColor: '#f9a8d4',
                width: '500px',
                height: '500px',
                bottom: '-8rem',
                left: '25%',
                pointerEvents: 'none',
              }}
            />
          </div>
        )}

        {/* Content container */}
        <div
          data-info-content
          className="relative"
          style={{ zIndex: 10, padding: resolvedLeftSlot ? 0 : '3rem 4rem' }}
        >
          {resolvedLeftSlot ? (
            // When provided, leftSlot takes over the entire left panel (including background/layout).
            // data-info-content is a grid on desktop; span all rows so the slot fills the whole panel.
            <div style={{ height: '100%', width: '100%', gridRow: '1 / -1', gridColumn: '1 / -1' }}>
              {resolvedLeftSlot}
            </div>
          ) : (
            <>
              {/* Top: Org branding */}
              <div className="flex items-center gap-3">
                {orgLogoHorizontalUrl ? (
                  <img
                    src={orgLogoHorizontalUrl}
                    alt={`${orgName} logo`}
                    style={{ height: '2.5rem', width: 'auto', objectFit: 'contain' }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : (
                  <>
                    <img
                      src={orgLogoUrl}
                      alt={`${orgName} logo`}
                      style={{ height: '2.5rem', width: 'auto', objectFit: 'contain' }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    <span style={{ fontSize: '1.125rem', fontWeight: 600, color: '#334155' }}>
                      {orgName}
                    </span>
                  </>
                )}
              </div>

              {/* Center: Welcome message and features */}
              <div style={{ maxWidth: '36rem' }}>
                <h1
                  style={{
                    fontSize: '2.5rem',
                    fontWeight: 700,
                    color: '#1e293b',
                    lineHeight: 1.2,
                  }}
                >
                  Welcome to your
                  <br />
                  <span style={{ color: primaryColor }}>Workspace</span>
                </h1>
                <p style={{ marginTop: '1rem', fontSize: '1.125rem', color: '#475569' }}>
                  Your integrated platform for seamless collaboration and productivity.
                </p>

                {/* Feature list with small icons */}
                <ul
                  style={{
                    marginTop: '2rem',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.75rem',
                  }}
                >
                  {[
                    'Access all your tools in one secure place',
                    'Collaborate seamlessly with your team',
                    'Stay organized with intelligent workflows',
                  ].map((text, i) => (
                    <li
                      key={i}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        color: '#475569',
                      }}
                    >
                      <span
                        style={{
                          flexShrink: 0,
                          width: '1.25rem',
                          height: '1.25rem',
                          borderRadius: '9999px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: `${primaryColor}20`,
                        }}
                      >
                        <CheckIcon
                          style={{ width: '0.75rem', height: '0.75rem', color: primaryColor }}
                        />
                      </span>
                      <span>{text}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Bottom: Footer */}
              <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                Powered by {poweredByLabel}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Right 1/3: Login Card - Full width on mobile, 1/3 on large screens */}
      <div
        data-login-card
        className={`flex items-center justify-center ${loginCardClassName}`}
        style={{}}
      >
        <div style={{ width: '100%', maxWidth: '24rem' }}>
          {/* Floating card */}
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '1rem',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(0, 0, 0, 0.05)',
              padding: '2rem',
            }}
          >
            {/* Org Logo & Name - Smaller, cleaner */}
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <img
                src={orgLogoUrl}
                alt={`${orgName} logo`}
                style={{
                  height: '3rem',
                  width: 'auto',
                  margin: '0 auto 0.75rem',
                  objectFit: 'contain',
                  display: 'block',
                }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
              <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#0f172a' }}>{orgName}</h2>
              <p style={{ marginTop: '0.25rem', fontSize: '0.875rem', color: '#64748b' }}>
                {subtitle}
              </p>
            </div>

            {/* Error Banner */}
            {errorText && (
              <div
                role="alert"
                style={{
                  marginBottom: '1.5rem',
                  padding: '0.75rem',
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '0.75rem',
                }}
              >
                <p style={{ fontSize: '0.875rem', color: '#dc2626' }}>{errorText}</p>
              </div>
            )}

            {/* Loading Indicator */}
            {isLoading && (
              <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'center' }}>
                <div
                  style={{
                    width: '1.5rem',
                    height: '1.5rem',
                    borderRadius: '9999px',
                    border: '2px solid #e2e8f0',
                    borderTopColor: primaryColor,
                    animation: 'spin 1s linear infinite',
                  }}
                />
              </div>
            )}

            {/* Form Slot */}
            <div>{children}</div>

            {/* Support Footer */}
            {(supportEmail || supportPhone) && (
              <div
                style={{
                  marginTop: '1.5rem',
                  paddingTop: '1.5rem',
                  borderTop: '1px solid #f1f5f9',
                  textAlign: 'center',
                }}
              >
                <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>
                  Need help?
                </p>
                <p style={{ fontSize: '0.875rem', color: '#64748b' }}>
                  {supportEmail && (
                    <a
                      href={`mailto:${supportEmail}`}
                      style={{ textDecoration: 'none', color: 'inherit' }}
                      onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                      onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
                    >
                      {supportEmail}
                    </a>
                  )}
                  {supportEmail && supportPhone && (
                    <span style={{ margin: '0 0.5rem', color: '#cbd5e1' }}>•</span>
                  )}
                  {supportPhone && (
                    <a
                      href={`tel:${supportPhone.replace(/\s/g, '')}`}
                      style={{ textDecoration: 'none', color: 'inherit' }}
                      onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                      onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
                    >
                      {supportPhone}
                    </a>
                  )}
                </p>
              </div>
            )}
          </div>

          {/* Mobile-only: Powered by footer outside card */}
          <p
            className="lg:hidden"
            style={{
              marginTop: '1.5rem',
              textAlign: 'center',
              fontSize: '0.75rem',
              color: '#94a3b8',
            }}
          >
            Powered by {poweredByLabel}
          </p>
        </div>
      </div>

      {/* CSS for layout */}
      <style>{`
        /* Keep login shell flush to viewport without scrollbars */
        html,
        body,
        #root {
          height: 100%;
        }
        [data-login-shell] {
          display: grid;
          grid-template-columns: 1fr;
          grid-template-rows: 100%;
          height: 100vh;
          min-height: 100vh;
          max-width: 100vw;
          overflow: hidden;
        }
        body {
          margin: 0;
          overflow-x: hidden;
          max-width: 100vw;
        }

        /* Mobile: hide info panel, show only login card */
        [data-info-panel] {
          display: none;
          overflow: hidden;
          max-width: 100%;
          overflow-x: hidden;
        }
        [data-login-card] {
          display: flex;
          align-items: center;
          justify-content: center;
          box-sizing: border-box;
          padding: 1.5rem;
          position: relative;
          z-index: 20;
          height: 100%;
        }

        /* Desktop: show both panels side by side */
        @media (min-width: 1024px) {
          body {
            overflow-y: hidden;
          }
          [data-login-shell] {
            grid-template-columns: 2fr 1fr !important;
            grid-template-rows: 100% !important;
          }
          [data-info-panel] {
            display: block !important;
            grid-column: 1;
            grid-row: 1;
            height: 100%;
            display: flex !important;
            flex-direction: column;
            overflow: hidden;
          }
          [data-info-content] {
            position: absolute;
            inset: 0;
            display: grid;
            grid-template-rows: auto minmax(0, 1fr) auto;
            height: 100%;
            flex: 1;
            box-sizing: border-box;
          }
          [data-info-content] > *:nth-child(2) {
            align-self: center;
          }
          [data-login-card] {
            grid-column: 2;
            grid-row: 1;
            height: 100%;
          }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

/**
 * Small check icon for feature list
 */
function CheckIcon({ style }: { style?: React.CSSProperties }) {
  return (
    <svg style={style} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
    </svg>
  );
}

/**
 * Utility to adjust a hex color by a percentage
 * Positive = lighter, Negative = darker
 */
function adjustColor(hex: string, amount: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.min(255, Math.max(0, (num >> 16) + amount));
  const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00ff) + amount));
  const b = Math.min(255, Math.max(0, (num & 0x0000ff) + amount));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}
